//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';

import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { OriginalLink } from '@components/molecules/smart-input/link-shortening/OriginalLink';
import { ShortLink } from '@components/molecules/smart-input/link-shortening/ShortLink';
import { FacebookPageMention } from '@components/molecules/smart-input/mentions/FacebookPageMention';
import { InstagramMention } from '@components/molecules/smart-input/mentions/InstagramMention';
import { Mention } from '@components/molecules/smart-input/mentions/Mention';
import { IDraftDecoratorComponentProps } from '@components/molecules/smart-input/smartInputModel';
import { SocialTextEntityType } from '@utils/social-text-utils/socialTextUtilsModel';
import { isCuralateShortLink } from '@utils/urlValidation';

interface DecoratedTextProps extends IDraftDecoratorComponentProps {
  children: React.ReactNode;
}

/**
 * This component is rendered when text matches a pattern provided in the composite decorator
 */
export const DecoratedText = ({ children, ...props }: DecoratedTextProps) => {
  const smartInputContext = useSmartInputContext();
  const type = smartInputContext.socialTextParseMethod(props.decoratedText)[0]
    ?.type;

  switch (type) {
    case SocialTextEntityType.Url: {
      if (smartInputContext.linkShortening?.isEnabled) {
        if (isCuralateShortLink(props.decoratedText)) {
          return <ShortLink {...props}>{children}</ShortLink>;
        }

        return <OriginalLink {...props}>{children}</OriginalLink>;
      }

      return <>{children}</>;
    }

    case SocialTextEntityType.Mention: {
      if (smartInputContext.mentions?.isEnabled) {
        return <Mention {...props}>{children}</Mention>;
      }

      return <>{children}</>;
    }

    case SocialTextEntityType.FacebookPageMention: {
      if (smartInputContext.mentions?.isEnabled) {
        return <FacebookPageMention {...props}>{children}</FacebookPageMention>;
      }

      return <>{children}</>;
    }

    case SocialTextEntityType.Hashtag:
    case SocialTextEntityType.InstagramMention: {
      if (smartInputContext.mentions?.isInstagramUserEmbedEnabled) {
        return <InstagramMention {...props}>{children}</InstagramMention>;
      }

      return <>{children}</>;
    }
    default:
      return <>{children}</>;
  }
};
